import React, { useState, useEffect, useRef, useMemo, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuthInfo } from '@/utils/auth'
import { clearSession, ensureProjectContext } from '@/utils/session'
import {
  getCouponUsedCountTodayYesterday,
  getMembersCountToday,
  getStampsCountEarnTodayYesterday,
  getTodayString,
  calcChangePercent
} from '@/api/dashboard'
import { createProjectCustomer, searchMember } from '@/api/member'
import {
  isMemberSearchKeyword,
  isSignupPhoneKeyword,
  MEMBER_KEYWORD_MAX_LENGTH
} from '@/utils/phone'
import { getProjectContext } from '@/utils/projectContext'
import {
  buildCustomerCreateRequest,
  buildSimpleSignupConfirmMessage,
  canCreateGuestCustomer,
  MEMBER_NOT_FOUND_MESSAGE,
  needsSignupPhoneConfirm,
  resolveNavigateAfterCreate
} from '@/utils/memberSignup'
import styles from './Dashboard.module.css'

/** 통계 카드 한 건 타입 */
interface StatItem {
  value: string
  changePercent: number | null
  changeLabel?: string
  isPositive: boolean | null
}

/** 화면용 목 데이터 - 사용자명 등 API 연동 전 */
const MOCK_USER = { name: '김매니저' }

function buildStatFromPercent(
  value: string,
  todayCount: number,
  yesterdayCount: number
): StatItem {
  const percent = calcChangePercent(todayCount, yesterdayCount)
  return {
    value,
    changePercent: percent,
    isPositive: percent === null ? null : percent >= 0
  }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [storeName, setStoreName] = useState<string>('로딩중...')
  const [outletCode, setOutletCode] = useState<string>('-')
  const [userName, setUserName] = useState<string>('로딩중...')

  const [statsLoading, setStatsLoading] = useState(true)
  const [stats, setStats] = useState<{
    newMember: StatItem
    points: StatItem
    coupon: StatItem
    totalMember: StatItem
  } | null>(null)

  const [searchValue, setSearchValue] = useState('')
  const [searchSubmitting, setSearchSubmitting] = useState(false)
  const [errorToast, setErrorToast] = useState<string | null>(null)

  const today = useMemo(() => getTodayString(), [])
  const { startAt, endAt } = useMemo(() => {
    const [year, month, day] = today.split('-').map(Number)
    const nextDay = new Date(year, month - 1, day + 1)
    const nextYear = nextDay.getFullYear()
    const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0')
    const nextDate = String(nextDay.getDate()).padStart(2, '0')

    return {
      startAt: `${today}T00:00:00+09:00`,
      endAt: `${nextYear}-${nextMonth}-${nextDate}T00:00:00+09:00`
    }
  }, [today])

  // 매장/사용자 정보 (로그인 시 저장된 값 사용)
  useEffect(() => {
    const authInfo = getAuthInfo()
    if (authInfo?.shopName != null) setStoreName(authInfo.shopName)
    if (authInfo?.outletCode != null) setOutletCode(authInfo.outletCode)
    setUserName(MOCK_USER.name)
  }, [])

  // ProjectContext 없으면 lookup으로 복구 (새로고침 등)
  useEffect(() => {
    let cancelled = false
    ensureProjectContext().catch((err) => {
      if (cancelled) return
      setErrorToast(
        err instanceof Error ? err.message : '프로젝트 정보를 불러오지 못했습니다.'
      )
    })
    return () => {
      cancelled = true
    }
  }, [])

  // 통계 (회원 수·적립 수·쿠폰 사용 수 API 병렬 호출)
  useEffect(() => {
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()

    if (!projectCode || !shopCode) {
      setStoreName(storeName => (storeName === '로딩중...' ? '매장 정보 없음' : storeName))
      setStats({
        newMember: { value: '-', changePercent: null, isPositive: null },
        points: { value: '-', changePercent: null, isPositive: null },
        coupon: { value: '-', changePercent: null, isPositive: null },
        totalMember: { value: '-', changePercent: null, isPositive: null, changeLabel: '전체' }
      })
      setStatsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      const [membersResult, stampsResult, couponResult] = await Promise.allSettled([
        getMembersCountToday(projectCode, shopCode, startAt, endAt),
        getStampsCountEarnTodayYesterday(projectCode, shopCode, startAt, endAt),
        getCouponUsedCountTodayYesterday(projectCode, shopCode, startAt, endAt)
      ])

      if (cancelled) return

      const members = membersResult.status === 'fulfilled' ? membersResult.value : null
      const stamps = stampsResult.status === 'fulfilled' ? stampsResult.value : null
      const coupons = couponResult.status === 'fulfilled' ? couponResult.value : null

      const isAuthError = (err: Error) =>
        /401|403|인증|로그인/.test(err.message)

      if (membersResult.status === 'rejected') {
        const err = membersResult.reason as Error
        if (isAuthError(err)) {
          setStatsLoading(false)
          clearSession()
          navigate('/auth/login')
          return
        }
        setErrorToast(err.message)
      }
      if (stampsResult.status === 'rejected') {
        const err = stampsResult.reason as Error
        if (isAuthError(err)) {
          setStatsLoading(false)
          clearSession()
          navigate('/auth/login')
          return
        }
        setErrorToast(err.message)
      }
      if (couponResult.status === 'rejected') {
        const err = couponResult.reason as Error
        if (isAuthError(err)) {
          setStatsLoading(false)
          clearSession()
          navigate('/auth/login')
          return
        }
        setErrorToast(err.message)
      }

      setStats({
        newMember: members
          ? buildStatFromPercent(
              `${members.todayCount}명`,
              members.todayCount,
              members.yesterdayCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        points: stamps
          ? buildStatFromPercent(
              `${stamps.todayCount}건`,
              stamps.todayCount,
              stamps.yesterdayCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        coupon: coupons
          ? buildStatFromPercent(
              `${coupons.todayCount}건`,
              coupons.todayCount,
              coupons.yesterdayCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        totalMember: members
          ? {
              value: `${members.totalCount}명`,
              changePercent: null,
              isPositive: null,
              changeLabel: '전체'
            }
          : { value: '-', changePercent: null, isPositive: null, changeLabel: '전체' }
      })
      setStatsLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [today, startAt, endAt, navigate])

  // 검색창 포커스
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const showError = (message: string) => {
    setErrorToast(message)
    const t = setTimeout(() => setErrorToast(null), 3000)
    return () => clearTimeout(t)
  }

  const handleStatClick = (type: string) => {
    console.log(`${type} 상세 보기`)
    // 상세 페이지 또는 모달 연결 예정
  }

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = searchValue.trim()
    if (!trimmed) {
      showError('바코드 또는 전화번호를 입력해주세요.')
      return
    }
    if (!isMemberSearchKeyword(trimmed)) {
      showError(`숫자만 입력해주세요. (최대 ${MEMBER_KEYWORD_MAX_LENGTH}자리)`)
      return
    }
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const projectContext = getProjectContext()
    if (!projectCode || !shopCode) {
      showError('매장 정보가 없습니다. 다시 로그인해 주세요.')
      return
    }
    if (!projectContext) {
      showError('프로젝트 정보가 없습니다. 다시 로그인해 주세요.')
      return
    }
    setSearchSubmitting(true)
    try {
      const data = await searchMember(projectCode, projectContext, trimmed)

      if (data.found) {
        navigate('/dashboard/member', {
          state: {
            customerCode: data.customerCode,
            nickname: data.nickname,
            status: data.status
          }
        })
        return
      }

      if (!canCreateGuestCustomer(data, projectContext)) {
        showError(MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      const createScope = data.createScope!
      let createBody
      try {
        createBody = buildCustomerCreateRequest(projectContext, data, trimmed)
      } catch (buildErr) {
        showError(buildErr instanceof Error ? buildErr.message : MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      if (createScope === 'BRAND') {
        const created = await createProjectCustomer(projectCode, createBody)
        const nav = resolveNavigateAfterCreate(createScope, data, created)
        navigate('/dashboard/member', { state: nav })
        return
      }

      if (!needsSignupPhoneConfirm(createScope) || !isSignupPhoneKeyword(trimmed)) {
        showError(MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      if (!window.confirm(buildSimpleSignupConfirmMessage(trimmed))) {
        return
      }

      const created = await createProjectCustomer(projectCode, createBody)
      const nav = resolveNavigateAfterCreate(createScope, data, created)
      navigate('/dashboard/member', { state: nav })
      return
    } catch (err) {
      showError(err instanceof Error ? err.message : '회원 조회에 실패했습니다.')
    } finally {
      setSearchSubmitting(false)
    }
  }

  const handleLogout = () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      clearSession()
      navigate('/auth/login')
    }
  }

  const renderStatChange = (item: StatItem) => {
    if (item.changePercent === null && !item.changeLabel) return null
    const changeClass =
      item.isPositive === null
        ? styles.statChangeNeutral
        : item.isPositive
          ? styles.statChangePositive
          : styles.statChangeNegative
    const arrow = item.isPositive === true ? '▲' : item.isPositive === false ? '▼' : '-'
    const text = item.changeLabel ?? `${Math.abs(item.changePercent ?? 0)}%`
    return (
      <div className={`${styles.statChange} ${changeClass}`}>
        <span className={styles.changeArrow}>{arrow}</span>
        <span>{text}</span>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>S</div>
          <div className={styles.storeInfo}>
            <div className={styles.storeName}>{storeName}</div>
            <div className={styles.storeCode}>{outletCode}</div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.userInfo}>
            <div className={styles.userIcon}>👤</div>
            <div className={styles.userName}>{userName}</div>
          </div>
          <button type="button" className={styles.logoutButton} onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </header>

      <main className={styles.mainContainer}>
        <section className={styles.statsSection}>
          <div
            className={styles.statCard}
            onClick={() => handleStatClick('new-member')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('new-member')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 신규 회원</div>
              <div className={`${styles.statIcon} ${styles.statIconNewMember}`}>👥</div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.newMember.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.newMember)}
          </div>

          <div
            className={styles.statCard}
            onClick={() => handleStatClick('points')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('points')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 적립 현황</div>
              <div className={`${styles.statIcon} ${styles.statIconPoints}`}>💰</div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.points.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.points)}
          </div>

          <div
            className={styles.statCard}
            onClick={() => handleStatClick('coupon')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('coupon')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 쿠폰 사용</div>
              <div className={`${styles.statIcon} ${styles.statIconCoupon}`}>🎟️</div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.coupon.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.coupon)}
          </div>

          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>전체 회원</div>
              <div className={`${styles.statIcon} ${styles.statIconTotalMember}`}>📊</div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.totalMember.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.totalMember)}
          </div>
        </section>

        <section className={styles.searchSection}>
          <h2 className={styles.searchTitle}>회원 조회</h2>
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <div className={styles.searchInputWrapper}>
              <span className={styles.searchIcon} aria-hidden>🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="바코드 또는 전화번호를 입력하세요"
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={MEMBER_KEYWORD_MAX_LENGTH}
                value={searchValue}
                onChange={(e) =>
                  setSearchValue(
                    e.target.value.replace(/\D/g, '').slice(0, MEMBER_KEYWORD_MAX_LENGTH)
                  )
                }
              />
            </div>
            <button type="submit" className={styles.searchButton} disabled={searchSubmitting}>
              {searchSubmitting ? '조회 중...' : '회원 조회'}
            </button>
          </form>
          <p className={styles.searchHint}>* 바코드 스캔 또는 전화번호 입력이 가능합니다</p>
        </section>
      </main>

      {errorToast && (
        <div className={styles.errorToast} role="alert">
          {errorToast}
          <button type="button" className={styles.errorToastClose} onClick={() => setErrorToast(null)} aria-label="닫기">
            ×
          </button>
        </div>
      )}
    </div>
  )
}
